import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import SearchableSelect from '../../components/SearchableSelect';
import ProveedorSelectorField from '../../components/ProveedorSelectorField';
import UbicacionSearch from '../../components/UbicacionSearch';
import { useAutoUbicacionPredeterminada } from '../../hooks/useAutoUbicacionPredeterminada';
import productoService, { OPTIONS_PRODUCTO_OPERATIVO, PARAMS_PRODUCTO_OPERATIVO } from '../../services/productoService';
import ordenCompraService from '../../services/ordenCompraService';
import CompraPendienteHiloInfoDialog from '../../components/compras/CompraPendienteHiloInfoDialog';
import comprasSeguimientoService, {
  CompraPendienteMaster,
  cantidadSugeridaDesdePendiente,
  costoBrutoDesdePendienteMaster,
  formatComprobanteMasterDisplay,
  formatFechaEmisionComprobanteMaster,
  porcDescDesdePendienteMaster,
} from '../../services/comprasSeguimientoService';
import { formatCurrency } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';

const FORMAS_DE_PAGO = [
  'CONTADO',
  'CTA.CTE.',
  'EFECTIVO',
  'DEVOLUCION',
  'CUOTAS',
  'CONTRAREEMBO',
  'PAGOANTICIPA',
];

const ITEM_NOTAS_MAX = 500;

interface OcItemForm {
  codigoArticulo: string;
  descripcion: string;
  cantidad: number;
  costoBrutoUnitario: number;
  porcDesc1: number;
  porcDesc2: number;
  porcDesc3: number;
  porcDesc4: number;
  porcDesc5: number;
  /** Corresponde a items.ite_notas (varchar 500). */
  notas?: string;
  nroFormularioMaster?: number;
  nroItemMaster?: number;
}

interface SucursalVentaOption {
  nroSuc: number;
  nombreSucursal?: string;
}

interface OcFormularioForm {
  nroSuc: number;
  nroProveedor: number;
  nombre?: string;
  cuit?: string;
  descripcion?: string;
  fechaEmision: string;
  fechaVencim?: string;
  formaDePago: string;
  nroUbicacionDestino?: number;
  porcDesc: number;
  porcDesc2: number;
  porcDesc3: number;
}

const defaultItem: OcItemForm = {
  codigoArticulo: '',
  descripcion: '',
  cantidad: 1,
  costoBrutoUnitario: 0,
  porcDesc1: 0,
  porcDesc2: 0,
  porcDesc3: 0,
  porcDesc4: 0,
  porcDesc5: 0,
  notas: '',
};

const todayIsoDate = (): string => new Date().toISOString().split('T')[0];

const STORAGE_NRO_SUC_OC = 'nroSuc';

const DecimalTextField: React.FC<{
  value: number;
  onChange: (v: number) => void;
  placeholder?: string;
  sx?: object;
  inputProps?: object;
}> = ({ value, onChange, placeholder, sx, inputProps }) => {
  const [localValue, setLocalValue] = useState<string>(String(value ?? 0));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setLocalValue(String(value ?? 0));
  }, [value, focused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = String(e.target.value ?? '');
    raw = raw.replace(/[^0-9.,]/g, '').replace(/,/g, '.');
    const parts = raw.split('.');
    if (parts.length > 2) raw = `${parts[0]}.${parts.slice(1).join('')}`;
    setLocalValue(raw);
    if (raw === '' || raw === '.' || raw.endsWith('.')) return;
    const n = Number(raw);
    if (!Number.isNaN(n) && Number.isFinite(n)) onChange(n);
  };

  const handleBlur = () => {
    setFocused(false);
    const currentV = Number(value ?? 0);
    const raw = String(localValue ?? '').trim();
    const normalized = raw.endsWith('.') ? raw.slice(0, -1) : raw;
    if (normalized === '' || normalized === '.') {
      setLocalValue('0');
      if (currentV !== 0) onChange(0);
      return;
    }
    const n = Number(normalized);
    const finalV = Number.isNaN(n) ? 0 : n;
    setLocalValue(String(finalV));
    if (finalV !== currentV) onChange(finalV);
  };

  return (
    <TextField
      size="small"
      type="text"
      value={localValue}
      onChange={handleChange}
      onFocus={() => setFocused(true)}
      onBlur={handleBlur}
      placeholder={placeholder}
      sx={sx}
      inputProps={{
        inputMode: 'decimal',
        ...inputProps,
      }}
    />
  );
};

const OrdenCompraForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [formulario, setFormulario] = useState<OcFormularioForm>({
    nroSuc: 0,
    nroProveedor: 0,
    nombre: '',
    cuit: '',
    descripcion: '',
    fechaEmision: todayIsoDate(),
    fechaVencim: todayIsoDate(),
    formaDePago: 'CONTADO',
    nroUbicacionDestino: undefined,
    porcDesc: 0,
    porcDesc2: 0,
    porcDesc3: 0,
  });

  const [proveedorNombre, setProveedorNombre] = useState('');
  const [ubicacionNombre, setUbicacionNombre] = useState('');
  const [openUbicacionSearch, setOpenUbicacionSearch] = useState(false);

  const [itemsProductos, setItemsProductos] = useState<OcItemForm[]>([]);
  const [accordionProductosExpanded, setAccordionProductosExpanded] = useState(true);
  const [saving, setSaving] = useState(false);

  const [openProductSearch, setOpenProductSearch] = useState(false);
  const [productSearchIndex, setProductSearchIndex] = useState<number | null>(null);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productos, setProductos] = useState<any[]>([]);
  const [productosPage, setProductosPage] = useState(0);
  const [productosRowsPerPage] = useState(10);
  const [productosTotalCount, setProductosTotalCount] = useState(0);

  const [openPendientes, setOpenPendientes] = useState(false);
  const [loadingPendientes, setLoadingPendientes] = useState(false);
  const [pendientes, setPendientes] = useState<CompraPendienteMaster[]>([]);
  const [pendienteHiloInfo, setPendienteHiloInfo] = useState<{
    nroFormularioMaster: number;
    nroItemMaster: number;
    resumenLinea?: string;
  } | null>(null);

  const [sucursales, setSucursales] = useState<SucursalVentaOption[]>([]);
  const [loadingSucursales, setLoadingSucursales] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingSucursales(true);
        const all: SucursalVentaOption[] = [];
        let page = 1;
        const pageSize = 20;
        while (true) {
          const response = await api.get('/api/SucursalesVenta', {
            params: { PageNumber: page, PageSize: pageSize },
          });
          const data = response.data;
          const batch: SucursalVentaOption[] = Array.isArray(data?.items) ? data.items : [];
          all.push(...batch);
          const totalPages = typeof data?.totalPages === 'number' ? data.totalPages : 1;
          if (page >= totalPages || batch.length === 0) break;
          page += 1;
        }
        setSucursales(all);
      } catch {
        setSucursales([]);
      } finally {
        setLoadingSucursales(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (sucursales.length === 0) return;
    if (formulario.nroSuc > 0) return;
    const stored = parseInt(localStorage.getItem(STORAGE_NRO_SUC_OC) || '', 10);
    const fromStore = Number.isFinite(stored) && stored > 0 ? stored : null;
    const match = fromStore ? sucursales.find((s) => s.nroSuc === fromStore) : undefined;
    const sorted = [...sucursales].sort((a, b) => a.nroSuc - b.nroSuc);
    const pick = match?.nroSuc ?? sorted[0]?.nroSuc;
    if (pick) setFormulario((f) => ({ ...f, nroSuc: pick }));
  }, [sucursales, formulario.nroSuc]);

  useEffect(() => {
    const v = Number(formulario.nroSuc);
    if (Number.isFinite(v) && v > 0) {
      localStorage.setItem(STORAGE_NRO_SUC_OC, String(v));
    }
  }, [formulario.nroSuc]);

  const handleFormularioChange = (updates: Partial<OcFormularioForm>) => {
    setFormulario((f) => ({ ...f, ...updates }));
  };

  const updateItemProducto = (index: number, updates: Partial<OcItemForm>) => {
    setItemsProductos((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  };

  const removeItemProducto = (index: number) => {
    setItemsProductos((prev) => prev.filter((_, i) => i !== index));
  };

  const addItemProducto = () => {
    setItemsProductos((prev) => [...prev, { ...defaultItem }]);
  };

  const subtotalItem = (item: OcItemForm) => {
    const qty = Number(item.cantidad) || 0;
    const costo = Number(item.costoBrutoUnitario) || 0;
    const d1 = 1 - (Number(item.porcDesc1) || 0) / 100;
    const d2 = 1 - (Number(item.porcDesc2) || 0) / 100;
    const d3 = 1 - (Number(item.porcDesc3) || 0) / 100;
    const d4 = 1 - (Number(item.porcDesc4) || 0) / 100;
    const d5 = 1 - (Number(item.porcDesc5) || 0) / 100;
    return Math.round(qty * costo * d1 * d2 * d3 * d4 * d5 * 100) / 100;
  };

  const subtotalBruto = itemsProductos.reduce((sum, it) => sum + subtotalItem(it), 0);
  const factorBonif =
    (1 - (Number(formulario.porcDesc) || 0) / 100) *
    (1 - (Number(formulario.porcDesc2) || 0) / 100) *
    (1 - (Number(formulario.porcDesc3) || 0) / 100);
  const totalBonificacion = Math.round(subtotalBruto * (1 - factorBonif) * 100) / 100;
  const subtotalNeto = Math.round((subtotalBruto - totalBonificacion) * 100) / 100;

  const sucursalesOrdenadas = useMemo(
    () => [...sucursales].sort((a, b) => a.nroSuc - b.nroSuc),
    [sucursales]
  );

  const handleProveedorSelect = (prov: any) => {
    setFormulario((f) => ({
      ...f,
      nroProveedor: prov.nroProveedor,
      nombre: prov.nombre,
      cuit: prov.cuit,
    }));
    setProveedorNombre(prov.nombre || '');
  };

  const handleProveedorClear = () => {
    setFormulario((f) => ({
      ...f,
      nroProveedor: 0,
      nombre: '',
      cuit: '',
    }));
    setProveedorNombre('');
  };

  const handleUbicacionSelect = (u: any) => {
    setFormulario((f) => ({
      ...f,
      nroUbicacionDestino: u.nroUbicacion,
    }));
    setUbicacionNombre(u.nombreUbicacion || '');
    setOpenUbicacionSearch(false);
  };

  const applyUbicacionPredeterminada = useCallback((nro: number, nombre: string) => {
    setFormulario((f) => ({ ...f, nroUbicacionDestino: nro }));
    setUbicacionNombre(nombre);
  }, []);

  useAutoUbicacionPredeterminada({
    nroUbicacionDestino: formulario.nroUbicacionDestino,
    onApply: applyUbicacionPredeterminada,
  });

  const loadProductos = useCallback(async () => {
    if (!openProductSearch) return;
    try {
      const res = await productoService.getProductos({
        searchTerm: productSearchTerm || undefined,
        pageNumber: productosPage + 1,
        pageSize: productosRowsPerPage,
        ...PARAMS_PRODUCTO_OPERATIVO,
      });
      setProductos(res.items || []);
      setProductosTotalCount(res.totalCount || 0);
    } catch {
      setProductos([]);
      setProductosTotalCount(0);
    }
  }, [openProductSearch, productSearchTerm, productosPage, productosRowsPerPage]);

  useEffect(() => {
    if (openProductSearch) loadProductos();
  }, [openProductSearch, loadProductos]);

  const handleSelectProduct = (p: any) => {
    const idx = productSearchIndex;
    if (idx === null) return;
    const codigo = p.codigoArticulo || p.codigo || '';
    const desc = p.descripcion || p.descripcionArticulo || '';
    const costo = Number(p.costo) || Number(p.costoBrutoUnitario) || 0;
    updateItemProducto(idx, {
      codigoArticulo: String(codigo),
      descripcion: String(desc),
      costoBrutoUnitario: costo,
    });
    setOpenProductSearch(false);
    setProductSearchIndex(null);
  };

  const openBuscarPendientes = async () => {
    if (!formulario.nroProveedor || formulario.nroProveedor <= 0) {
      enqueueSnackbar('Primero seleccione un proveedor', { variant: 'warning' });
      return;
    }
    setOpenPendientes(true);
    setLoadingPendientes(true);
    try {
      const data = await comprasSeguimientoService.getPendientesMaster(formulario.nroProveedor, true);
      setPendientes(data);
    } catch (e) {
      enqueueSnackbar(getApiErrorMessage(e, 'No se pudieron cargar pendientes'), { variant: 'error' });
      setPendientes([]);
    } finally {
      setLoadingPendientes(false);
    }
  };

  const agregarPendienteLinea = async (p: CompraPendienteMaster) => {
    const cantidad = cantidadSugeridaDesdePendiente(p);
    let costoBruto = costoBrutoDesdePendienteMaster(p);
    const { porcDesc1, porcDesc2, porcDesc3, porcDesc4, porcDesc5 } = porcDescDesdePendienteMaster(p);
    if (costoBruto <= 0 && (p.codigoArticulo || '').trim()) {
      try {
        const pr = await productoService.getProducto((p.codigoArticulo || '').trim(), OPTIONS_PRODUCTO_OPERATIVO);
        costoBruto = Number((pr as any).costo) || Number((pr as any).costoBrutoUnitario) || 0;
      } catch {
        /* sin maestro ni stock */
      }
    }
    setItemsProductos((prev) => [
      ...prev,
      {
        ...defaultItem,
        codigoArticulo: p.codigoArticulo || '',
        descripcion: p.descripcionArticulo || '',
        cantidad,
        costoBrutoUnitario: costoBruto,
        porcDesc1,
        porcDesc2,
        porcDesc3,
        porcDesc4,
        porcDesc5,
        nroFormularioMaster: p.nroFormularioMaster,
        nroItemMaster: p.nroItemMaster,
      },
    ]);
  };

  const handleCodigoBlurOrEnter = useCallback(
    async (idx: number, codigoValue: string) => {
      const codigo = (codigoValue || '').trim();
      if (!codigo) return;
      try {
        const p = await productoService.getProducto(codigo, OPTIONS_PRODUCTO_OPERATIVO);
        const desc = (p as any).descripcion || (p as any).descripcionArticulo || '';
        const costo = Number((p as any).costo) || Number((p as any).costoBrutoUnitario) || 0;
        setItemsProductos((prev) => {
          const next = [...prev];
          if (!next[idx]) return prev;
          next[idx] = { ...next[idx], codigoArticulo: codigo, descripcion: desc, costoBrutoUnitario: costo };
          return next;
        });
      } catch {
        enqueueSnackbar(`No se encontró el producto con código "${codigo}"`, { variant: 'warning' });
      }
    },
    [enqueueSnackbar]
  );

  const handleSubmit = async () => {
    if (!formulario.nroProveedor || formulario.nroProveedor <= 0) {
      enqueueSnackbar('Debe seleccionar un proveedor', { variant: 'warning' });
      return;
    }
    if (!formulario.nroSuc || formulario.nroSuc <= 0) {
      enqueueSnackbar('Debe seleccionar una sucursal de venta', { variant: 'warning' });
      return;
    }
    if (!loadingSucursales && sucursalesOrdenadas.length === 0) {
      enqueueSnackbar('No hay sucursales de venta configuradas. Cargue sucursales en el sistema.', { variant: 'warning' });
      return;
    }
    if (!formulario.nroUbicacionDestino || formulario.nroUbicacionDestino <= 0) {
      enqueueSnackbar('Debe seleccionar una ubicación destino válida', { variant: 'warning' });
      return;
    }
    if (!formulario.formaDePago?.trim()) {
      enqueueSnackbar('Debe seleccionar una forma de pago', { variant: 'warning' });
      return;
    }

    const itemsValidos = itemsProductos.filter(
      (it) =>
        (it.codigoArticulo || '').trim().length > 0 &&
        Number(it.cantidad) > 0 &&
        Number(it.costoBrutoUnitario) > 0
    );

    if (itemsValidos.length === 0) {
      enqueueSnackbar('Debe agregar al menos un ítem válido (código, cantidad y costo unitario > 0)', {
        variant: 'warning',
      });
      return;
    }

    const payload = {
      formulario: {
        nroSuc: Number(formulario.nroSuc),
        nroProveedor: Number(formulario.nroProveedor),
        nombre: formulario.nombre || undefined,
        cuit: formulario.cuit || undefined,
        descripcion: formulario.descripcion?.trim() || undefined,
        fechaEmision: formulario.fechaEmision || todayIsoDate(),
        fechaVencim: formulario.fechaVencim || undefined,
        formaDePago: formulario.formaDePago,
        nroUbicacionDestino: formulario.nroUbicacionDestino,
        porcDesc: Number(formulario.porcDesc) || 0,
        porcDesc2: Number(formulario.porcDesc2) || 0,
        porcDesc3: Number(formulario.porcDesc3) || 0,
      },
      items: itemsValidos.map((it) => ({
        codigoArticulo: it.codigoArticulo.trim(),
        descripcion: it.descripcion?.trim() || undefined,
        cantidad: Number(it.cantidad),
        costoBrutoUnitario: Number(it.costoBrutoUnitario),
        porcDesc1: Number(it.porcDesc1) || 0,
        porcDesc2: Number(it.porcDesc2) || 0,
        porcDesc3: Number(it.porcDesc3) || 0,
        porcDesc4: Number(it.porcDesc4) || 0,
        porcDesc5: Number(it.porcDesc5) || 0,
        ...(it.nroFormularioMaster != null &&
        it.nroItemMaster != null &&
        it.nroFormularioMaster > 0 &&
        it.nroItemMaster > 0
          ? { nroFormularioMaster: it.nroFormularioMaster, nroItemMaster: it.nroItemMaster }
          : {}),
        ...(() => {
          const n = (it.notas || '').trim();
          return n ? { notas: n.slice(0, ITEM_NOTAS_MAX) } : {};
        })(),
      })),
    };

    try {
      setSaving(true);
      const created = await ordenCompraService.createOrdenCompra(payload);
      enqueueSnackbar('Orden de compra creada correctamente', { variant: 'success' });
      navigate(`/compras/ordenes/${created.nroFormulario}`);
    } catch (error: unknown) {
      const msg = getApiErrorMessage(error, 'Error al crear la orden de compra');
      enqueueSnackbar(msg, { variant: 'error', autoHideDuration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  const handleSucursalChange = (v: number | null) => {
    handleFormularioChange({ nroSuc: v != null && v > 0 ? v : 0 });
  };

  const fechaEmisionStr = formulario.fechaEmision || todayIsoDate();
  const fechaVencimStr = formulario.fechaVencim || '';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
            Nueva Orden de Compra
          </Typography>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Encabezado
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }} alignItems="flex-end">
            <Grid item xs={12} md={6}>
              <ProveedorSelectorField
                label="Proveedor"
                required
                value={
                  formulario.nroProveedor > 0 && proveedorNombre
                    ? {
                        nroProveedor: formulario.nroProveedor,
                        nombre: proveedorNombre,
                        cuit: formulario.cuit || undefined,
                      }
                    : null
                }
                onSelect={handleProveedorSelect}
                onClear={handleProveedorClear}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <SearchableSelect<number>
                label="Nº Sucursal"
                required
                disabled={loadingSucursales || sucursalesOrdenadas.length === 0}
                loading={loadingSucursales}
                allowEmpty
                emptyOptionLabel={
                  loadingSucursales
                    ? 'Cargando…'
                    : sucursalesOrdenadas.length === 0
                      ? 'Sin sucursales'
                      : 'Seleccione'
                }
                value={formulario.nroSuc > 0 ? formulario.nroSuc : null}
                onChange={handleSucursalChange}
                options={sucursalesOrdenadas.map((s) => ({
                  value: s.nroSuc,
                  label: `${s.nroSuc} - ${s.nombreSucursal?.trim() || `Sucursal ${s.nroSuc}`}`,
                }))}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Fecha emisión"
                type="date"
                value={fechaEmisionStr}
                onChange={(e) => handleFormularioChange({ fechaEmision: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Fecha vencimiento"
                type="date"
                value={fechaVencimStr}
                onChange={(e) => handleFormularioChange({ fechaVencim: e.target.value || undefined })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SearchableSelect
                label="Forma de pago"
                required
                value={formulario.formaDePago || 'CONTADO'}
                onChange={(v) => handleFormularioChange({ formaDePago: v ?? 'CONTADO' })}
                options={FORMAS_DE_PAGO.map((fp) => ({ value: fp, label: fp }))}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label="Ubicación destino"
                value={ubicacionNombre || ''}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setOpenUbicacionSearch(true)} edge="end" size="small">
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onClick={() => setOpenUbicacionSearch(true)}
                required
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <TextField
                fullWidth
                label="Observaciones"
                value={formulario.descripcion || ''}
                onChange={(e) => handleFormularioChange({ descripcion: e.target.value })}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Detalle
          </Typography>
          {!formulario.nroProveedor || formulario.nroProveedor <= 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Seleccione un proveedor para poder cargar ítems.
            </Typography>
          ) : null}
          <Box
            sx={{
              mb: 2,
              opacity: !formulario.nroProveedor || formulario.nroProveedor <= 0 ? 0.5 : 1,
              pointerEvents: !formulario.nroProveedor || formulario.nroProveedor <= 0 ? 'none' : 'auto',
            }}
          >
            <Accordion expanded={accordionProductosExpanded} onChange={(_, exp) => setAccordionProductosExpanded(!!exp)}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  flexDirection: 'row-reverse',
                  '& .MuiAccordionSummary-expandIconWrapper': { marginRight: 0, marginLeft: 0.5 },
                  '& .MuiAccordionSummary-content': { margin: 0, marginRight: 0 },
                }}
              >
                <Typography sx={{ fontWeight: 600, color: '#424242' }}>Items de Compras</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                        <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Notas</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Cant.
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Costo unit.
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          % Desc 1
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">
                          Subtotal
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itemsProductos.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                            Sin ítems. Use &quot;Agregar producto&quot; para añadir líneas.
                          </TableCell>
                        </TableRow>
                      ) : (
                        itemsProductos.map((item, idx) => (
                          <TableRow key={`p-${idx}`}>
                            <TableCell>
                              <TextField
                                size="small"
                                value={item.codigoArticulo || ''}
                                onChange={(e) => updateItemProducto(idx, { codigoArticulo: e.target.value })}
                                onBlur={(e) => handleCodigoBlurOrEnter(idx, (e.target as HTMLInputElement).value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleCodigoBlurOrEnter(idx, (e.target as HTMLInputElement).value);
                                  }
                                }}
                                placeholder="Código"
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        size="small"
                                        onClick={() => {
                                          setProductSearchIndex(idx);
                                          setOpenProductSearch(true);
                                        }}
                                      >
                                        <SearchIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                                sx={{ minWidth: 120 }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                fullWidth
                                value={item.descripcion || ''}
                                onChange={(e) => updateItemProducto(idx, { descripcion: e.target.value })}
                                placeholder="Descripción"
                              />
                            </TableCell>
                            <TableCell sx={{ minWidth: 180 }}>
                              <TextField
                                size="small"
                                fullWidth
                                value={item.notas ?? ''}
                                onChange={(e) =>
                                  updateItemProducto(idx, {
                                    notas: e.target.value.slice(0, ITEM_NOTAS_MAX),
                                  })
                                }
                                placeholder="Notas"
                                inputProps={{ maxLength: ITEM_NOTAS_MAX }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ width: 90 }}>
                              <DecimalTextField
                                value={item.cantidad ?? 1}
                                onChange={(v) => updateItemProducto(idx, { cantidad: v })}
                                inputProps={{ min: 0.01, step: 0.01 }}
                                sx={{ width: 80 }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ width: 110 }}>
                              <DecimalTextField
                                value={item.costoBrutoUnitario ?? 0}
                                onChange={(v) => updateItemProducto(idx, { costoBrutoUnitario: v })}
                                inputProps={{ min: 0, step: 1 }}
                                sx={{ width: 120 }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ width: 80 }}>
                              <DecimalTextField
                                value={item.porcDesc1 ?? 0}
                                onChange={(v) => updateItemProducto(idx, { porcDesc1: v })}
                                inputProps={{ min: 0, max: 100, step: 0.01 }}
                                sx={{ width: 70 }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {formatCurrency(subtotalItem(item))}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton size="small" onClick={() => removeItemProducto(idx)} color="error">
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button
                    startIcon={<AddIcon />}
                    onClick={addItemProducto}
                    variant="outlined"
                    sx={{ color: '#1976d2', borderColor: '#1976d2' }}
                  >
                    Agregar producto
                  </Button>
                  <Button variant="outlined" onClick={openBuscarPendientes} sx={{ borderColor: '#1976d2', color: '#1976d2' }}>
                    Buscar pendientes
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Box>

          <Typography variant="h6" sx={{ mb: 2, mt: 3, color: '#1976d2' }}>
            Totales
          </Typography>
          <Card sx={{ mt: 2, boxShadow: 2, borderRadius: 2, mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Subtotal Bruto:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    {formatCurrency(subtotalBruto)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'grey.300', bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 800, color: 'text.secondary' }}>
                  Bonificación Aplicada
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                  <DecimalTextField
                    value={formulario.porcDesc ?? 0}
                    onChange={(v) => handleFormularioChange({ porcDesc: v })}
                    sx={{ width: 80 }}
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    %
                  </Typography>
                  <DecimalTextField
                    value={formulario.porcDesc2 ?? 0}
                    onChange={(v) => handleFormularioChange({ porcDesc2: v })}
                    sx={{ width: 80 }}
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    %
                  </Typography>
                  <DecimalTextField
                    value={formulario.porcDesc3 ?? 0}
                    onChange={(v) => handleFormularioChange({ porcDesc3: v })}
                    sx={{ width: 80 }}
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                  />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    %
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    Total Bonificación Aplicada:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                    {formatCurrency(totalBonificacion)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Subtotal Neto:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    {formatCurrency(subtotalNeto)}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button variant="outlined" onClick={() => navigate('/compras/ordenes')} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={saving}
              sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#1565c0' } }}
            >
              {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Guardar'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <UbicacionSearch open={openUbicacionSearch} onClose={() => setOpenUbicacionSearch(false)} onSelect={handleUbicacionSelect} />

      <Dialog open={openPendientes} onClose={() => setOpenPendientes(false)} maxWidth="md" fullWidth>
        <DialogTitle>Buscar pendientes</DialogTitle>
        <DialogContent>
          {loadingPendientes ? (
            <CircularProgress size={22} sx={{ display: 'block', my: 2 }} />
          ) : (
            <TableContainer sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Master</TableCell>
                    <TableCell>Comprobante</TableCell>
                    <TableCell>Fecha emisión</TableCell>
                    <TableCell>Artículo</TableCell>
                    <TableCell align="right">Cant. pedida</TableCell>
                    <TableCell align="right">Pend. facturar</TableCell>
                    <TableCell align="right">Pend. recibir</TableCell>
                    <TableCell align="center" sx={{ width: 44 }} />
                    <TableCell align="center">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendientes.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                        No hay líneas pendientes para este proveedor.
                      </TableCell>
                    </TableRow>
                  ) : (
                    pendientes.map((p) => (
                    <TableRow key={`${p.nroFormularioMaster}-${p.nroItemMaster}`}>
                      <TableCell>
                        {p.nroFormularioMaster}/{p.nroItemMaster}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatComprobanteMasterDisplay(p)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatFechaEmisionComprobanteMaster(p.fechaEmisionComprobanteMaster)}</TableCell>
                      <TableCell>
                        {p.codigoArticulo} - {p.descripcionArticulo}
                      </TableCell>
                      <TableCell align="right">{p.cantidadPedida}</TableCell>
                      <TableCell align="right">{p.pendienteFacturar}</TableCell>
                      <TableCell align="right">{p.pendienteRecibir}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Ver documentos facturados y remitidos">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setPendienteHiloInfo({
                                nroFormularioMaster: p.nroFormularioMaster,
                                nroItemMaster: p.nroItemMaster,
                                resumenLinea: `${p.codigoArticulo ?? ''} - ${p.descripcionArticulo ?? ''}`.trim(),
                              })
                            }
                            aria-label="Información facturado y remitido"
                          >
                            <InfoOutlinedIcon fontSize="small" color="info" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Button size="small" onClick={() => void agregarPendienteLinea(p)}>
                          Agregar
                        </Button>
                      </TableCell>
                    </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPendientes(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <CompraPendienteHiloInfoDialog
        open={!!pendienteHiloInfo}
        onClose={() => setPendienteHiloInfo(null)}
        nroFormularioMaster={pendienteHiloInfo?.nroFormularioMaster ?? null}
        nroItemMaster={pendienteHiloInfo?.nroItemMaster ?? null}
        resumenLinea={pendienteHiloInfo?.resumenLinea}
      />

      <Dialog
        open={openProductSearch}
        onClose={() => {
          setOpenProductSearch(false);
          setProductSearchIndex(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Buscar artículo</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} sx={{ mb: 2, mt: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar por código o descripción..."
              value={productSearchTerm}
              onChange={(e) => setProductSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setProductosPage(0);
                  loadProductos();
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              onClick={() => {
                setProductosPage(0);
                loadProductos();
              }}
              sx={{ color: '#1976d2', borderColor: '#1976d2' }}
            >
              Buscar
            </Button>
          </Stack>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Descripción</TableCell>
                  <TableCell align="center">Seleccionar</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productos.map((p) => (
                  <TableRow key={p.codigoArticulo || p.codigo || ''} hover>
                    <TableCell>{p.codigoArticulo || p.codigo || '-'}</TableCell>
                    <TableCell>{p.descripcion || p.descripcionArticulo || '-'}</TableCell>
                    <TableCell align="center">
                      <Button size="small" onClick={() => handleSelectProduct(p)}>
                        Seleccionar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={productosTotalCount}
            page={productosPage}
            onPageChange={(_, p) => setProductosPage(p)}
            rowsPerPage={productosRowsPerPage}
            onRowsPerPageChange={() => {}}
            rowsPerPageOptions={[10]}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenProductSearch(false);
              setProductSearchIndex(null);
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrdenCompraForm;
